import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import { cartQueryKey } from "./useCart";
import { putCartItem } from "../../api/cart";

/**
 * DEBOUNCE_MS — задержка перед сетевым PUT после последнего изменения qty:
 * быстрые тапы «+/-» в карточке не должны рождать запрос на каждый (тот же
 * приём, что и debounce поиска, docs/07-miniapp-ux.md §2.2).
 */
const DEBOUNCE_MS = 400;

/**
 * useSetCartItem — меняет количество позиции корзины с debounce сетевого
 * запроса. Компонент отвечает за мгновенный локальный отклик (qty меняется
 * в UI сразу, до того как запрос уйдёт в сеть); этот хук лишь гарантирует,
 * что на быструю серию тапов по одному товару уйдёт один PUT с последним
 * значением, а результат обновит общий кэш корзины (["cart"]) для всех
 * экранов витрины сразу.
 */
export function useSetCartItem() {
  const queryClient = useQueryClient();
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  return useCallback(
    (productId: string, qty: number) => {
      const existing = timers.current.get(productId);
      if (existing) {
        clearTimeout(existing);
      }

      const timer = setTimeout(() => {
        timers.current.delete(productId);
        putCartItem(productId, qty)
          .then((cart) => queryClient.setQueryData(cartQueryKey, cart))
          .catch(() => {
            // Сеть или сервис отклонили изменение (например, товар стал недоступен):
            // локальный оптимистичный qty был неверным — перечитываем настоящую корзину.
            void queryClient.invalidateQueries({ queryKey: cartQueryKey });
          });
      }, DEBOUNCE_MS);
      timers.current.set(productId, timer);
    },
    [queryClient],
  );
}
